import { useEffect, useState } from "react"
import useDebounce from "./use-debounce"
import useEventListener from "./use-event-listener"

const unknown = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  height: 0,
  width: 0
}

const getSelectionRect = () => {
  try {
    // getRangeAt(0) can raise if there is no selection, so we'll just return
    // the unkown object instead.
    return window
      .getSelection()
      .getRangeAt(0)
      .getBoundingClientRect()
  } catch (e) {
    return unknown
  }
}

/**
 * Takes an object and makes it respond to functions that make it look like
 * a DOM element for positioning purposes. The only real use case is wrapping
 * the selection position.
 * @param {Object} rect
 */
const wrapRect = rect => {
  return {
    get clientWidth() {
      return rect.width
    },
    get clientHeight() {
      return rect.height
    },
    getBoundingClientRect() {
      return rect
    }
  }
}

const useSelectionRect = ({ debounce }) => {
  const [rect, setRect] = useState(unknown)

  const [update] = useDebounce(() => {
    if (debounce > -1) setRect(getSelectionRect())
  }, debounce)

  useEventListener(document, "selectionchange", update)
  useEventListener(window, "resize", update)

  return rect
}

/**
 * When making text bold, italic, or other small transformations, the selection
 * changes slightly (usually fractions, or just a few pixels). This moving
 * around is slightly annoying, so we use this buffer to only move the
 * selection if the values changed by more than 5 pixels.
 *
 * TODO: Ideally, this would happen at a different level, but we're not
 * abstracting the buffer until we use it again somewhere.
 */
const differentEnoughBuffer = 5
const differentEnough = (rect1, rect2) => {
  for (let key in rect1) {
    const diff = Math.abs(rect1[key] - rect2[key])
    if (diff > differentEnoughBuffer) return true
  }
  return false
}

const useSelectionReference = ({ debounce }) => {
  const rect = useSelectionRect({ debounce })
  const [reference, setReference] = useState(wrapRect(rect))

  useEffect(() => {
    const oldRect = reference.getBoundingClientRect()
    if (differentEnough(oldRect, rect)) {
      setReference(wrapRect(rect))
    }
  }, [rect])
  return reference
}

const getBoundingClientRect = element => {
  if (!element) return unknown
  return element.getBoundingClientRect()
}

const getOffsetParent = element => {
  return (element && element.offsetParent) || document.documentElement
}

const usePosition = (target, reference, dependancies) => {
  const [data, setData] = useState({ styles: {}, arrowStyles: {} })

  useEffect(() => {
    const offsetParent = getOffsetParent(target)
    const targetRect = getBoundingClientRect(target)
    const offsetRect = getBoundingClientRect(offsetParent)
    const referenceRect = getBoundingClientRect(reference)

    let left = referenceRect.left - offsetRect.left + referenceRect.width / 2
    let arrowLeft = targetRect.width / 2

    // TODO: handle top/bottom collisions. For our purposes, these aren't as
    // common right now.
    const collisions = {
      left: false,
      right: false
    }

    // collision on the left if the position is smaller than half the width
    // of the toolbar (because we translate the toolbar 50%)
    if (targetRect.width / 2 > left) collisions.left = true

    // collision on the right if the position is larger than the container
    // width while accounting for the size of the toolbar and 50% translation.
    if (offsetRect.width - targetRect.width / 2 < left) collisions.right = true

    if (collisions.left) {
      arrowLeft =
        referenceRect.left - offsetRect.left + referenceRect.width / 2 - 10
      left = targetRect.width / 2 + 10
    } else if (collisions.right) {
      arrowLeft = left - (offsetRect.width - targetRect.width) + 10
      left = offsetRect.width - targetRect.width / 2 - 10
    }

    setData({
      styles: {
        bottom:
          offsetRect.bottom - referenceRect.top - 5 - offsetParent.scrollTop,
        left
      },
      arrowStyles: {
        left: arrowLeft
      }
    })
  }, [target, reference, ...dependancies])

  return data
}

export const useSelectionPosition = (target, { debounce }, dependancies) => {
  const reference = useSelectionReference({ debounce })
  return usePosition(target, reference, dependancies)
}
